import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import updatePartNumber from "@tggstore/Store/Handlers/Custom/UpdateMyPartNumber";
import { CustomState } from "@tggstore/Store/Reducers";
import * as React from "react";
import { FC, useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// Verndale Customization: Ticket TGGMS-386
// Product Detail Page
const mapStateToProps = (state: CustomState) => {
    const { isAuthenticated, rememberMe, isGuest } = getSession(state);
    const userIsAuthenticated = (isAuthenticated || rememberMe) && !isGuest;
    return {
        userIsAuthenticated,
    };
};

const mapDispatchToProps = {
    updatePartNumber,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasProduct & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDetailsCustomerPartNumberStyles {
    labelText?: TypographyProps;
    valueText?: TypographyProps;
    textfield?: TextFieldProps;
    editValueText?: TypographyProps;
    editLabelText?: TypographyProps;
    editableLink?: LinkPresentationProps;
    NoProductNumberlabelText?: TypographyProps;
}

export const customerPartNumberStyles: ProductDetailsCustomerPartNumberStyles = {
    labelText: {
        size: 16,
        weight: "bold",
        css: css`
            margin-right: 0;
        `,
    },
    valueText: {
        size: 16,
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-start;
            margin-bottom: 5px;
            gap: 15px;
        `,
    },
    textfield: {
        cssOverrides: {
            formField: css`
                width: 20%;
            `,
        },
        css: css`
            height: 100% !important;
        `,
    },
    editValueText: {
        size: 16,
        css: css`
            margin-bottom: 5px;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 8px;
        `,
    },
    editLabelText: {
        size: 16,
        css: css`
            font-weight: bold;
            margin: 0;
            width: 12%;
        `,
    },
    editableLink: {
        css: css`
            font-size: 16px;
            cursor: pointer;
            display: flex;
            align-items: center;

            &:focus {
                outline: none;
            }
        `,
    },
    NoProductNumberlabelText: {
        size: 16,
        weight: "bold",
        css: css`
            margin-right: 25px;
        `,
    },
};

const styles = customerPartNumberStyles;

const ProductDetailsCustomerPartNumber: FC<Props> = ({ product, updatePartNumber, userIsAuthenticated }) => {
    // Verndale Customization: Ticket TGGMS-386
    // Product Detail Page
    const [isEditable, setIsEditable] = useState(false);
    const [customerProductNumber, setCustomerProductNumber] = useState("");

    if (!userIsAuthenticated) {
        return null;
    }

    useEffect(() => {
        if (product.customerProductNumber !== "") {
            setCustomerProductNumber(product.customerProductNumber);
        }
    }, [product.customerProductNumber]);

    const handleEditClick = () => {
        setIsEditable(true);
    };

    const handleSaveClick = useCallback(
        (e: any) => {
            const erpProductNumber = product.productNumber;

            updatePartNumber({
                erpNumber: erpProductNumber,
                customerPartNumber: customerProductNumber,
            });
            setIsEditable(false);
        },
        [customerProductNumber],
    );
    const onTextfieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomerProductNumber(event.target.value);
    };
    if (isEditable) {
        return (
            <StyledWrapper {...styles.editValueText} data-test-selector="customerProductNumber">
                <Typography {...styles.editLabelText} as="p">
                    {translate("My Part #")}
                </Typography>
                <TextField
                    {...styles.textfield}
                    value={customerProductNumber}
                    type="text"
                    data-test-selector="product_myPart#"
                    onChange={onTextfieldChange}
                />
                <Link {...styles.editableLink} onClick={handleSaveClick}>
                    {translate("Save")}
                </Link>
            </StyledWrapper>
        );
    }
    // End Verndale Customization

    return (
        <StyledWrapper>
            <Typography {...styles.valueText} as="p" data-test-selector="customerProductNumber">
                <Typography {...(customerProductNumber !== "" ? styles.labelText : styles.NoProductNumberlabelText)}>
                    {translate("My Part #")}
                </Typography>
                {customerProductNumber}
                <Link {...styles.editableLink} onClick={handleEditClick}>
                    {translate("Edit")}
                </Link>
            </Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductDetailsCustomerPartNumber)),
    definition: {
        displayName: "Customer Part Number",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
